/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-unused-expressions */
/* eslint-disable react/no-children-prop */
/* eslint-disable @typescript-eslint/no-shadow */

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Form from 'components/common/Form';
import { Typography, Divider, Box, Button, Modal } from '@mui/material';
import { BaseDrawer } from 'components/styleguide';
import { FormButton } from 'components/common/Form/Fields';
import { useDispatch } from 'react-redux';
import { useBulkUpdateTracksClearancesMutation } from 'services/clear/tracks';
import { updateTrackCueContainerInStore } from 'redux-core/cueContainers/actions';
import { useState, useEffect } from 'react';
import { showErrorToast, showSuccessToast } from 'components/common/toastNotifications';
import { fetchFooterInfo } from 'redux-core/overview/actions';
import CircularProgress from '@mui/material/CircularProgress';
import enhancer from './enhancer';
import TrackList from './TrackList/index';
import RightsOptionsList from './SelectRightsOptions/index';
import { CommonDrawerPropTypes } from '../constants';

const rootT = 'drawers.productionSetup.bulkRightsOptions';

const Dialog = ({ open, onClose, onConfirm, t }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    await onConfirm();
    setIsProcessing(false);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'background.paper',
          padding: 4,
          width: 400,
          textAlign: 'center',
          flexDirection: 'column',
        }}
      >
        <Typography sx={{ mb: 3 }}>{t(`${rootT}.dialog`)}</Typography>
        {isProcessing && <CircularProgress />}
        {!isProcessing && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button color="primary" onClick={onClose}>
              {t(`${rootT}.no`)}
            </Button>
            <Button variant="contained" color="primary" onClick={handleConfirm}>
              {t(`${rootT}.yes`)}
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

const BulkRightsOptionsDrawer = ({
  onClose,
  payload: { productionId, subprojectId },
  setBulkRightsOptions,
  bulkRightOptions,
}) => {
  const { t } = useTranslation();

  const [bulkUpdate] = useBulkUpdateTracksClearancesMutation();

  const dispatch = useDispatch();

  const onCancel = () => {
    onClose();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const [openDialog, setOpenDialog] = useState(false);

  const handleSubmit = async () => {
    setOpenDialog(true);
  };

  useEffect(
    () => () => {
      dispatch(
        setBulkRightsOptions({
          rightsOptionsToUpdate: [],
          tracksIds: [],
          subProjectId: null,
        }),
      );
    },
    [dispatch, setBulkRightsOptions],
  );

  const confirm = async () => {
    dispatch(
      setBulkRightsOptions({
        ...bulkRightOptions,
        subProjectId: subprojectId,
      }),
    );
    if (bulkRightOptions.rightsOptionsToUpdate.length === 0 || bulkRightOptions.tracksIds.length === 0) {
      showErrorToast(t(`${rootT}.toastError`));
      return null;
    }
    try {
      const updatedBulkUpdate = await bulkUpdate({
        subprojectId,
        rightsOptionsToUpdate: bulkRightOptions.rightsOptionsToUpdate,
        used: true,
        tracksIds: bulkRightOptions.tracksIds,
      });
      updatedBulkUpdate.data.updatedFees.forEach((item) => {
        dispatch(
          updateTrackCueContainerInStore(item.trackId, {
            approvedFees: item.trackApprovedFees,
            estimatedFees: item.trackEstimatedFees,
            used: true,
          }),
        );
        dispatch(fetchFooterInfo());
      });

      showSuccessToast(t(`${rootT}.toastSuccess`));
    } catch (error) {
      showErrorToast(t(`${rootT}.toastErrorUpdate`));
    }

    setOpenDialog(false);
    onClose();
    return null;
  };

  const hasValidSelections = (options) => {
    const hasRightsOptions =
      Array.isArray(options?.rightsOptionsToUpdate) &&
      options.rightsOptionsToUpdate.length > 0 &&
      options.rightsOptionsToUpdate.some((option) => option.used === true);

    const hasSelectedTracks = Array.isArray(options?.tracksIds) && options.tracksIds.length > 0;

    return hasRightsOptions && hasSelectedTracks;
  };
  return (
    <Form onSubmit={handleSubmit} testId="bulk-rights-option-drawer">
      <Typography
        sx={{
          fontWeight: 'bold',
          fontSize: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        {t(`${rootT}.drawerTitle`)}
        <Box sx={{ gap: 2, flexDirection: 'row', display: 'flex' }}>
          <FormButton
            variant="secondary"
            size="xs"
            children="global.forms.applyChanges"
            testId="apply-button"
            alwaysEnabled
            disabled={!hasValidSelections(bulkRightOptions)}
            type="submit"
            style={{
              borderColor: '#a15ce2',
              color: hasValidSelections(bulkRightOptions) ? '#a15ce2' : '#D3D3D3',
              cursor: hasValidSelections(bulkRightOptions) ? 'pointer' : 'not-allowed',
            }}
          />
          <FormButton
            alwaysEnabled
            variant="primary"
            size="xs"
            testId="bulk-rights-option-drawer-close"
            children="global.forms.close"
            onClick={() => onCancel()}
          />
        </Box>
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography sx={{ display: 'flex', justifyContent: 'flex-start', flexDirection: 'row', fontSize: '0.875rem' }}>
        {t(`${rootT}.instructionalText`)}
      </Typography>

      <Box sx={{ mt: 0 }}>
        <Typography
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'row',
            fontSize: '1.25rem',
            fontWeight: 'bold',
            paddingTop: '1rem',
            lineHeight: 1.4,
          }}
        >
          {t(`${rootT}.step1`)}
        </Typography>
        <RightsOptionsList productionId={productionId} />
      </Box>

      <Box sx={{ mt: 0 }}>
        <Typography
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'row',
            fontSize: '1.25rem',
            fontWeight: 'bold',
            paddingTop: '1rem',
            lineHeight: 1.4,
          }}
        >
          {t(`${rootT}.step2`)}
        </Typography>
        <TrackList translate={t} subprojectId={subprojectId} />
      </Box>
      <Dialog open={openDialog} onClose={handleCloseDialog} onConfirm={confirm} t={t} />
    </Form>
  );
};

BulkRightsOptionsDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  payload: PropTypes.object,
};

const Drawer = ({ open, onClose, ...props }) => (
  <BaseDrawer open={open} onClose={onClose}>
    <BulkRightsOptionsDrawer onClose={onClose} {...props} />
  </BaseDrawer>
);

Drawer.propTypes = {
  ...CommonDrawerPropTypes,
};

export default enhancer(Drawer);
